import { useState } from 'react';
import { Link } from '../../components/base/Link';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function TextRedaction() {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState('');
  const [processedText, setProcessedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [mode, setMode] = useState<'highlight' | 'redact'>('highlight');

  const handleProcess = () => {
    setIsProcessing(true);
    // 模拟处理过程
    setTimeout(() => {
      if (mode === 'highlight') {
        // 高亮显示敏感信息
        const highlighted = inputText
          .replace(/张三|李四|王五/g, '<mark class="bg-red-200">$&</mark>')
          .replace(/1[3-9]\d{9}/g, '<mark class="bg-red-200">$&</mark>')
          .replace(/\d{17}[\dX]/g, '<mark class="bg-red-200">$&</mark>');
        setProcessedText(highlighted);
      } else {
        // 脱敏处理
        const redacted = inputText
          .replace(/张三|李四|王五/g, '***')
          .replace(/1[3-9]\d{9}/g, '***********')
          .replace(/\d{17}[\dX]/g, '******************');
        setProcessedText(redacted);
      }
      setIsProcessing(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* 科技背景 - 保持不变 */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900"></div>

        <div className="relative max-w-7xl mx-auto px-6 z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('textRedaction.heroTitle')}
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              {t('textRedaction.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* 功能说明区 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('textRedaction.feature1Title')}</h3>
              <p className="text-gray-600">{t('textRedaction.feature1Desc')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('textRedaction.feature2Title')}</h3>
              <p className="text-gray-600">{t('textRedaction.feature2Desc')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-stack-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('textRedaction.feature3Title')}</h3>
              <p className="text-gray-600">{t('textRedaction.feature3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 在线试用区 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('textRedaction.tryTitle')}</h2>
            <p className="text-lg text-gray-600">{t('textRedaction.trySubtitle')}</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* 模式切换 */}
            <div className="flex justify-center mb-6">
              <div className="bg-gray-100 rounded-lg p-1 flex">
                <button
                  onClick={() => setMode('highlight')}
                  className={`px-6 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                    mode === 'highlight' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('textRedaction.highlightMode')}
                </button>
                <button
                  onClick={() => setMode('redact')}
                  className={`px-6 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                    mode === 'redact' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('textRedaction.redactMode')}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* 输入区域 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('textRedaction.inputLabel')}
                </label>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder={t('textRedaction.inputPlaceholder')}
                  className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                  maxLength={500}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-gray-500">
                    {inputText.length}/500 {t('textRedaction.characters')}
                  </span>
                  <button
                    onClick={handleProcess}
                    disabled={!inputText.trim() || isProcessing}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {isProcessing ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        {t('textRedaction.processing')}
                      </>
                    ) : (
                      t('textRedaction.startButton')
                    )}
                  </button>
                </div>
              </div>

              {/* 输出区域 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('textRedaction.outputLabel')}
                </label>
                <div className="w-full h-64 p-4 border border-gray-300 rounded-lg bg-gray-50 overflow-y-auto text-sm">
                  {processedText ? (
                    <div dangerouslySetInnerHTML={{ __html: processedText }} />
                  ) : (
                    <div className="text-gray-400 italic">
                      {t('textRedaction.outputPlaceholder')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 示例文本 */}
            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">{t('textRedaction.exampleTitle')}</h4>
              <p className="text-sm text-gray-700 mb-3">
                "{t('textRedaction.exampleText')}"
              </p>
              <button
                onClick={() => setInputText(t('textRedaction.exampleText'))}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                {t('textRedaction.useExample')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 技术原理说明 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('textRedaction.techTitle')}</h2>
            <p className="text-lg text-gray-600">{t('textRedaction.techSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-brain-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('textRedaction.tech1Title')}</h3>
              <p className="text-gray-600">{t('textRedaction.tech1Desc')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-3xl text-brand-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('textRedaction.tech2Title')}</h3>
              <p className="text-gray-600">{t('textRedaction.tech2Desc')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-line text-3xl text-brand-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('textRedaction.tech3Title')}</h3>
              <p className="text-gray-600">{t('textRedaction.tech3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 应用案例 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('textRedaction.casesTitle')}</h2>
            <p className="text-lg text-gray-600">{t('textRedaction.casesSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-hospital-line text-xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('textRedaction.case1Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('textRedaction.case1Desc')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-scales-3-line text-xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('textRedaction.case2Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('textRedaction.case2Desc')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-survey-line text-xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('textRedaction.case3Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('textRedaction.case3Desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('textRedaction.ctaTitle')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {t('textRedaction.ctaSubtitle')}
          </p>
          <Link to="/pricing#contact" className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
            {t('textRedaction.ctaButton')}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
